import { useEffect, useState } from 'react';
import classNames from 'classnames';
import CountUp from 'react-countup';
import WidgetShell from '../WidgetShell/WidgetShell';
import WidgetLink from '../WidgetLink/WidgetLink';
import WidgetProgressRow from '../WidgetProgressRow/WidgetProgressRow';
import {
  TODO_PRIORITY,
  TODO_PRIORITY_LABELS,
  TODO_STATUS,
  TODO_STATUS_LABELS,
} from 'constants/todos';
import {
  TODO_ASSIGNMENT_COLORS,
  TODO_PRIORITY_COLORS,
  TODO_STATUS_COLORS,
} from 'constants/dashboard';

const PRIORITY_ORDER = [
  TODO_PRIORITY.URGENT,
  TODO_PRIORITY.HIGH,
  TODO_PRIORITY.MEDIUM,
  TODO_PRIORITY.LOW,
];

const STATUS_ORDER = [TODO_STATUS.INPROGRESS, TODO_STATUS.DONE];
const ROW_ANIMATION_DELAY_MS = 60;
const SECTION_STACK_CLASS = 'space-y-3';
const SECTION_TITLE_CLASS = 'text-xs font-medium text-gray-500 uppercase tracking-wider';
const ASSIGNMENT_BARS = [
  {
    key: 'assignedToReminders',
    label: 'Assigned to reminders',
    colorClass: TODO_ASSIGNMENT_COLORS.ASSIGNED.bar,
  },
  {
    key: 'controlsReminderBehavior',
    label: 'Controls reminders',
    colorClass: TODO_ASSIGNMENT_COLORS.CONTROLS.bar,
  },
];

const buildTodoLink = (filters = {}) => {
  const params = new URLSearchParams();

  Object.entries(filters).forEach(([key, value]) => {
    if (value) {
      params.set(key, value);
    }
  });

  const queryString = params.toString();

  return queryString ? `/todos?${queryString}` : '/todos';
};

function VerticalMetricBar({
  label,
  count,
  maxCount,
  colorClass,
  animationDelay,
  to,
  columnWidthClass = 'w-16',
  labelClassName = 'justify-center text-[11px]',
}) {
  const [isReady, setIsReady] = useState(false);
  const heightPct = maxCount > 0 ? (count / maxCount) * 100 : 0;

  useEffect(() => {
    const frameId = window.requestAnimationFrame(() => {
      setIsReady(true);
    });

    return () => window.cancelAnimationFrame(frameId);
  }, []);

  return (
    <div className={classNames('flex shrink-0 flex-col items-center gap-1', columnWidthClass)}>
      <div className="relative h-20 w-8 overflow-hidden rounded-xl bg-gray-800/70">
        <div
          className={classNames('absolute inset-x-0 bottom-0 rounded-t-xl transition-all duration-700 ease-out', colorClass)}
          style={{
            height: isReady ? `${heightPct}%` : '0%',
            transitionDelay: `${animationDelay}ms`,
          }}
        />
        <span className="absolute inset-0 flex items-center justify-center text-xs font-medium text-white tabular-nums">
          <CountUp
            end={count}
            duration={0.9}
            delay={animationDelay / 1000}
            preserveValue
          />
        </span>
      </div>
      {to ? (
        <WidgetLink
          to={buildTodoLink({ priority: to })}
          className={labelClassName}
        >
          {label}
        </WidgetLink>
      ) : (
        <span className={classNames('text-center text-[11px] font-medium leading-tight text-gray-300', labelClassName)}>
          {label}
        </span>
      )}
    </div>
  );
}

function TodosWidget({ statusData, priorityData, assignmentData, loading, error, onRetry }) {
  const hasStatusData = Boolean(statusData && Object.keys(statusData).length > 0);
  const hasPriorityData = Boolean(priorityData && Object.keys(priorityData).length > 0);
  const normalizedAssignmentData = {
    assignedToReminders: assignmentData?.assignedToReminders ?? 0,
    controlsReminderBehavior: assignmentData?.controlsReminderBehavior ?? 0,
  };
  const assignmentMax = Math.max(
    normalizedAssignmentData.assignedToReminders,
    normalizedAssignmentData.controlsReminderBehavior,
    0
  );
  const hasAssignmentData = assignmentMax > 0;
  const total = hasStatusData
    ? Object.values(statusData).reduce((sum, count) => sum + count, 0)
    : PRIORITY_ORDER.reduce((sum, priority) => sum + (priorityData?.[priority] || 0), 0);
  const visibleStatusTotal = STATUS_ORDER.reduce((sum, status) => sum + (statusData?.[status] || 0), 0);
  const maxPriorityCount = Math.max(...PRIORITY_ORDER.map((priority) => priorityData?.[priority] || 0), 1);
  const hasData = hasStatusData || hasPriorityData || hasAssignmentData;

  return (
    <WidgetShell
      className="h-full"
      title={(
        <WidgetLink to={buildTodoLink()}>
          Todos
        </WidgetLink>
      )}
      actions={
        !loading && !error && hasData ? (
          <span className="text-3xl font-bold leading-none text-white tabular-nums">
            <CountUp end={total} duration={1.2} preserveValue />
          </span>
        ) : null
      }
      loading={loading}
      error={error}
      onRetry={onRetry}
      empty={!hasData}
      emptyMessage="No todos yet"
    >
      <div className="space-y-4">
        <div className={SECTION_STACK_CLASS}>
          <h4 className={SECTION_TITLE_CLASS}>Status</h4>
          {STATUS_ORDER.map((status, index) => {
            const colors = TODO_STATUS_COLORS[status] || TODO_STATUS_COLORS.INPROGRESS;

            return (
              <WidgetProgressRow
                key={status}
                label={TODO_STATUS_LABELS[status]}
                count={statusData?.[status] || 0}
                total={visibleStatusTotal}
                colorClass={colors.bar}
                labelClassName={classNames('text-sm font-medium', colors.text)}
                animationDelay={index * ROW_ANIMATION_DELAY_MS}
              />
            );
          })}
        </div>

        <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between lg:gap-6">
          <div className={classNames(SECTION_STACK_CLASS, 'lg:flex-1')}>
            <h4 className={SECTION_TITLE_CLASS}>Priority</h4>
            <div className="flex items-end justify-start gap-1 sm:gap-2">
              {PRIORITY_ORDER.map((priority, index) => {
                const colors = TODO_PRIORITY_COLORS[priority] || TODO_PRIORITY_COLORS.LOW;

                return (
                  <VerticalMetricBar
                    key={priority}
                    label={TODO_PRIORITY_LABELS[priority]}
                    count={priorityData?.[priority] || 0}
                    maxCount={maxPriorityCount}
                    colorClass={colors.bar}
                    to={priority}
                    animationDelay={(STATUS_ORDER.length + index) * ROW_ANIMATION_DELAY_MS}
                  />
                );
              })}
            </div>
          </div>

          <div className={classNames(SECTION_STACK_CLASS, 'lg:w-72 lg:shrink-0')}>
            <h4 className={SECTION_TITLE_CLASS}>Assigned</h4>
            <div className="flex items-start justify-start gap-2">
              {ASSIGNMENT_BARS.map((bar, index) => (
                <VerticalMetricBar
                  key={bar.key}
                  label={bar.label}
                  count={normalizedAssignmentData[bar.key]}
                  maxCount={assignmentMax}
                  colorClass={bar.colorClass}
                  animationDelay={(STATUS_ORDER.length + PRIORITY_ORDER.length + index) * ROW_ANIMATION_DELAY_MS}
                  labelClassName="whitespace-normal px-1 text-center text-[10px] leading-3"
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    </WidgetShell>
  );
}

export default TodosWidget;
